import { TargetChipInput } from '@/forms/shared/TargetChipInput';
import { Alert, Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import type React from 'react';
import { useMemo } from 'react';
import type { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { CaseSelect } from '../fields/CaseSelect';
import type { ServiceTicketCreateSchemaType } from '../schema';
import { resolveLocationTargets } from '../targetResolution';
import { CaseOrLocationSection } from './CaseOrLocationSection';

interface TargetSectionProps {
  control: Control<ServiceTicketCreateSchemaType>;
  errors: FieldErrors<ServiceTicketCreateSchemaType>;
  setValue: UseFormSetValue<ServiceTicketCreateSchemaType>;
  watch: UseFormWatch<ServiceTicketCreateSchemaType>;
}

export const TargetSection = ({ control, errors, setValue, watch }: TargetSectionProps) => {
  const targetMode = watch('targetMode') ?? 'single';
  const targets = watch('targets') ?? [];

  // A shared case (if any) applies to every ticket in both modes, so it's left alone here -
  // only the fields owned exclusively by the mode being left are cleared.
  const handleModeChange = (
    _: React.SyntheticEvent,
    mode: ServiceTicketCreateSchemaType['targetMode'],
  ) => {
    if (mode === targetMode) return;

    setValue('targetMode', mode, { shouldValidate: false });

    if (mode === 'id') {
      setValue('location_id', '', { shouldValidate: false });
    } else {
      setValue('targets', [], { shouldValidate: false });
    }
  };

  const { duplicateGroups, unresolvable } = useMemo(
    () => resolveLocationTargets(targets),
    [targets],
  );

  return (
    <Paper variant="outlined" sx={{ mb: 4 }}>
      <Tabs
        value={targetMode}
        onChange={handleModeChange}
        sx={{ px: 2, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
      >
        <Tab
          value="single"
          label="Select Case or Location"
          data-test="service-ticket-target-mode-single"
          sx={{ minHeight: 40 }}
        />
        <Tab
          value="id"
          label="Enter Machine / Location IDs"
          data-test="service-ticket-target-mode-id"
          sx={{ minHeight: 40 }}
        />
      </Tabs>

      <Box sx={{ p: 2 }}>
        {targetMode === 'id' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Add one or more machine or location IDs to create service tickets in bulk. Priority,
            description, tasks, technician, route, and scheduled date below will be applied to every
            ticket created.
          </Alert>
        )}

        {targetMode === 'single' ? (
          <CaseOrLocationSection
            control={control}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
        ) : (
          <Box>
            <TargetChipInput
              errors={errors}
              setValue={setValue}
              watch={watch}
              dataTestPrefix="service-ticket-target-chip"
            />

            {unresolvable.length > 0 && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                data-test="service-ticket-target-unresolvable-notice"
              >
                <Typography variant="body2">
                  These machines have no installed location and can't be used to create a service
                  ticket - remove them to continue: {unresolvable.join(', ')}
                </Typography>
              </Alert>
            )}

            {duplicateGroups.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }} data-test="service-ticket-target-dedupe-notice">
                <Typography variant="body2">
                  Service tickets target locations, not machines. The following will each create
                  just one ticket:
                </Typography>
                {duplicateGroups.map((group) => (
                  <Typography key={group.locationId} variant="body2">
                    Location {group.locationId}: {group.sources.join(', ')}
                  </Typography>
                ))}
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <CaseSelect
                control={control}
                errors={errors}
                label="Related Case (Optional)"
                helperText="Applied to every ticket created in this batch."
              />
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
